/**
 * Slack-style alert thread mockup with a live escalation timer. Visitors can
 * click "Resolve" to stop the countdown — the interaction makes the
 * escalation behavior tactile in a way that screenshots can't.
 */
import { useEffect, useRef, useState } from 'react';
import { Bell, Check, Slack } from 'lucide-react';

const ESCALATE_AT_S = 30 * 60; // 30 minutes — same default as the real product

function fmtCountdown(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function AlertEscalationMockup() {
  const [resolved, setResolved] = useState(false);
  const [remaining, setRemaining] = useState(ESCALATE_AT_S - 7 * 60); // pretend 7 min in
  const t = useRef<number | null>(null);

  useEffect(() => {
    if (resolved) return;
    t.current = window.setInterval(() => {
      setRemaining((r) => Math.max(0, r - 60)); // tick by minutes for visual snappiness
    }, 1500);
    return () => {
      if (t.current) window.clearInterval(t.current);
    };
  }, [resolved]);

  const escalating = remaining < 5 * 60 && !resolved;

  return (
    <div className="overflow-hidden rounded-lg border bg-background/80 shadow-lg ring-1 ring-border/40">
      <div className="flex items-center justify-between border-b bg-muted/30 px-4 py-3 text-xs font-mono uppercase tracking-wider text-muted-foreground">
        <span className="flex items-center gap-2">
          <Slack className="h-4 w-4" />
          #deliverability-alerts
        </span>
        <span
          className={
            'flex items-center gap-1.5 rounded px-2 py-0.5 ' +
            (resolved
              ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
              : escalating
                ? 'bg-rose-500/10 text-rose-700 animate-pulse dark:text-rose-400'
                : 'bg-amber-500/10 text-amber-700 dark:text-amber-400')
          }
        >
          {resolved ? 'resolved' : escalating ? 'escalating' : 'open'}
        </span>
      </div>
      <div className="space-y-4 p-5">
        {/* Bot post */}
        <div className="flex gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Bell className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">InboxGuard</span> · 7 min ago · also sent to{' '}
              <span className="font-mono text-foreground">deliverability@acme.com</span>
            </div>
            <div className="mt-2 rounded-md border-l-4 border-rose-500 bg-rose-500/5 px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <span className="text-base font-semibold text-rose-700 dark:text-rose-400">
                  Critical · DMARC alignment dropped to 64%
                </span>
                <span className="flex-shrink-0 rounded bg-rose-500/10 px-2 py-0.5 text-xs font-mono uppercase text-rose-700 dark:text-rose-400">
                  P1
                </span>
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                <span className="font-mono text-foreground">acme.com</span> — was 98%, now 64% over the last 24h.
                {' '}A 3rd-party sender alignment likely broke. Look at HubSpot or Mailchimp first.
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button className="rounded bg-foreground px-3 py-1 text-xs font-medium text-background hover:bg-foreground/90">
                  Open in dashboard →
                </button>
                <button
                  className="rounded border px-3 py-1 text-xs font-medium hover:bg-muted"
                  onClick={() => setResolved(true)}
                  disabled={resolved}
                >
                  {resolved ? '✓ Resolved' : 'Mark resolved'}
                </button>
                <button className="rounded border px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-muted">
                  Snooze 1h
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Escalation timer line. Both states share one grid cell so the box
            always reserves the taller (unresolved) height — resolving no longer
            shrinks it and shoves the page content up. */}
        <div
          className={
            'grid rounded-md border px-4 py-3 text-sm ' +
            (resolved
              ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400'
              : escalating
                ? 'border-rose-500/40 bg-rose-500/5 text-rose-700 dark:text-rose-400'
                : 'border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-400')
          }
        >
          <span className={'col-start-1 row-start-1 flex items-center gap-3 ' + (resolved ? '' : 'invisible')}>
            <Check className="h-4 w-4 flex-shrink-0" />
            <span>Closed before escalation. No on-call page fired. Audit trail recorded.</span>
          </span>
          <span className={'col-start-1 row-start-1 flex items-start gap-3 ' + (resolved ? 'invisible' : '')}>
            <Bell className={'mt-0.5 h-4 w-4 flex-shrink-0 ' + (escalating ? 'animate-pulse' : '')} />
            <span>
              Escalating to on-call email + secondary Slack channel in{' '}
              <span className="rounded bg-current/10 px-1.5 py-0.5 font-mono font-semibold tabular-nums">{fmtCountdown(remaining)}</span>{' '}
              if no one resolves. Click <strong>Mark resolved</strong> above to stop the timer.
            </span>
          </span>
        </div>

        <div className="grid grid-cols-3 gap-2 border-t pt-4 text-center text-xs">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Email</div>
            <div className="mt-0.5 font-medium text-emerald-700 dark:text-emerald-400">delivered · 4 ppl</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Slack</div>
            <div className="mt-0.5 font-medium text-emerald-700 dark:text-emerald-400">posted · this thread</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Webhook</div>
            <div className="mt-0.5 font-medium text-emerald-700 dark:text-emerald-400">200 · PagerDuty</div>
          </div>
        </div>
      </div>
    </div>
  );
}
